import React, { useCallback, useMemo } from "react";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ui } from "../../../../../../../../../interfaces/interfaces";
import { PlanDetailsPrefix } from "@utils/BasicTypes";
import { KeysForPlanSideTabs, useCarePlanPlanDetailsSideTab } from "../../../../../../../../../hooks/sideTabs/useCarePlanPlanDetailsSideTab";
import useDebugInformation from "../../../../../../../../../hooks/useDebugInformation";
import { getIcon, a11yProps, getLabel } from "./helpers";

/**
 * Component for rendering side tabs in the Service Agreement Policy UI.
 * 
 * @param {Object} props - Component props
 * @param {string} props.parent - The parent component name
 * @param {PlanDetailsPrefix} props.prefix - The prefix for types
 */
const SideTabs: React.FC<Props> = ({ parent, prefix }) => {
    const {
        serviceAgreementPolicyTab,
        sideTabsStates,
        isBoiler,
        sideTabsIndexes
    } = useCarePlanPlanDetailsSideTab();

    const debugName = `${parent}▷[SideTabs]`;

    useDebugInformation(debugName, { name: 'SideTabs', prefix, serviceAgreementPolicyTab });

    /**
     * Handles tab change events.
     * 
     * @param {React.SyntheticEvent} event - The event object
     * @param {number} value - The new tab index
     */
    const handleChange = useCallback((event: React.SyntheticEvent<Element, Event>, value: any) => {
        // if (value !== undefined) ui.ServiceAgreementPlanDetailsSideTab = value;
        if (value !== undefined) ui.CarePlanPlanDetailsActiveSideTab = value;

    }, []);

    const renderedTabs = useMemo(() => Object.entries(sideTabsIndexes).map(([key, value]) => (
        !isBoiler && key === 'BOILER_SIDE_TAB' ? null :
            <Tab
                key={`tab-${key}-${value}`}
                label={getLabel(key)}
                {...a11yProps(value)}
                wrapped
                icon={getIcon(value, sideTabsStates[key as KeysForPlanSideTabs], isBoiler)}
                iconPosition="top"
                disabled={!isBoiler && key === 'BOILER_SIDE_TAB'}
            />
    )), [sideTabsIndexes, isBoiler, sideTabsStates]);

    return (
        <Tabs
            orientation="vertical"
            variant="scrollable"
            value={serviceAgreementPolicyTab}
            onChange={handleChange}
            aria-label="Vertical tabs for Service Agreement Plan Details"
            sx={{ borderRight: 1, borderColor: 'divider', button: { maxWidth: 75, minWidth: 75 } }}>
            {renderedTabs}
        </Tabs>
    );
};

export default SideTabs;

/**
 * Prop types for the SideTabs component.
 */
type Props = {
    parent: string,
    prefix: PlanDetailsPrefix
};
